/**
 * The reviewable list behind sections 2, 3 and 4 of the panel.
 *
 * One component renders symptoms, conditions, allergies, current medications,
 * complaints, differential diagnoses and suggested tests, because §2–§5 ask for
 * the same four verbs on all of them — confirm, edit, remove, add — and giving
 * each section its own affordances would make the same action look different
 * seven times.
 *
 * Two rules the component enforces on every row:
 *   - An AI-sourced row is labelled as one until a doctor rules on it.
 *   - A rejected row is kept and struck through, not deleted. The doctor can
 *     see what they dismissed, and the audit trail already records it.
 */

import { Check, Pencil, Plus, Trash2, Undo2, X } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import type { ClinicalAction, ClinicalBucket, ClinicalItem } from "@/types/copilot";

import { ProvenanceMark, SectionEmpty } from "./copilot-primitives";

/** Which fields each bucket shows, in display order. */
const DISPLAY: Record<ClinicalBucket, { primary: string; details: string[]; addLabel: string }> = {
  symptoms: {
    primary: "name",
    details: ["duration", "severity", "frequency", "notes"],
    addLabel: "Add a symptom",
  },
  conditions: { primary: "name", details: ["since", "notes"], addLabel: "Add a condition" },
  allergies: {
    primary: "substance",
    details: ["reaction", "severity"],
    addLabel: "Add an allergy",
  },
  medications: {
    primary: "name",
    details: ["dose", "frequency", "notes"],
    addLabel: "Add a medication the patient takes",
  },
  complaints: { primary: "text", details: ["notes"], addLabel: "Add a complaint" },
  diagnoses: {
    primary: "name",
    details: ["confidence", "rationale"],
    addLabel: "Add a diagnosis to consider",
  },
  tests: { primary: "name", details: ["category", "rationale"], addLabel: "Add a custom test" },
};

const DETAIL_LABEL: Record<string, string> = {
  duration: "Duration",
  severity: "Severity",
  frequency: "Frequency",
  since: "Since",
  reaction: "Reaction",
  dose: "Dose",
  confidence: "AI confidence",
  rationale: "Why",
  category: "Type",
  notes: "Note",
};

export function ClinicalList({
  bucket,
  items,
  onAct,
  onAdd,
  readOnly = false,
  showPrescriptionToggle = false,
}: {
  bucket: ClinicalBucket;
  items: ClinicalItem[];
  onAct: (
    bucket: ClinicalBucket,
    itemId: string,
    action: ClinicalAction,
    patch?: Record<string, string>,
  ) => void;
  onAdd: (bucket: ClinicalBucket, payload: Record<string, string>) => void;
  readOnly?: boolean | undefined;
  /** Tests only: offer "add to prescription" (§5 — the doctor orders, not the AI). */
  showPrescriptionToggle?: boolean | undefined;
}) {
  const [adding, setAdding] = useState(false);
  const config = DISPLAY[bucket];

  return (
    <div className="space-y-1.5">
      {items.length === 0 && !adding && (
        <SectionEmpty>
          Nothing recorded yet. Anything the AI picks up will appear here for you to review.
        </SectionEmpty>
      )}

      {items.map((item) => (
        <Row
          key={item.id}
          bucket={bucket}
          item={item}
          readOnly={readOnly}
          showPrescriptionToggle={showPrescriptionToggle}
          onAct={onAct}
        />
      ))}

      {adding ? (
        <AddForm
          bucket={bucket}
          onCancel={() => setAdding(false)}
          onSubmit={(payload) => {
            onAdd(bucket, payload);
            setAdding(false);
          }}
        />
      ) : (
        !readOnly && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 w-full justify-start gap-1.5 px-2 text-xs text-muted-foreground"
            onClick={() => setAdding(true)}
          >
            <Plus className="size-3.5" aria-hidden />
            {config.addLabel}
          </Button>
        )
      )}
    </div>
  );
}

function Row({
  bucket,
  item,
  readOnly,
  showPrescriptionToggle,
  onAct,
}: {
  bucket: ClinicalBucket;
  item: ClinicalItem;
  readOnly: boolean;
  showPrescriptionToggle: boolean;
  onAct: (
    bucket: ClinicalBucket,
    itemId: string,
    action: ClinicalAction,
    patch?: Record<string, string>,
  ) => void;
}) {
  const config = DISPLAY[bucket];
  const [editing, setEditing] = useState(false);
  const primary = (item[config.primary as keyof ClinicalItem] as string | undefined) ?? "—";
  const rejected = item.status === "rejected";

  if (editing) {
    return (
      <EditForm
        bucket={bucket}
        item={item}
        onCancel={() => setEditing(false)}
        onSubmit={(patch) => {
          onAct(bucket, item.id, "edit", patch);
          setEditing(false);
        }}
      />
    );
  }

  return (
    <div
      className={cn(
        "group rounded-lg border border-border bg-card px-3 py-2 transition-colors",
        rejected && "opacity-55",
        item.addedToPrescription && "border-success/30 bg-success/5",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <p className={cn("min-w-0 text-xs font-semibold", rejected && "line-through")}>{primary}</p>
        <ProvenanceMark source={item.source} status={item.status} />
      </div>

      {config.details.some((field) => item[field as keyof ClinicalItem]) && (
        <dl className="mt-1 space-y-0.5">
          {config.details.map((field) => {
            const value = item[field as keyof ClinicalItem] as string | undefined;
            if (!value) return null;
            return (
              <div key={field} className="flex gap-1.5 text-[0.7rem] leading-relaxed">
                <dt className="shrink-0 text-muted-foreground">{DETAIL_LABEL[field] ?? field}:</dt>
                <dd className="min-w-0 text-foreground">{value}</dd>
              </div>
            );
          })}
        </dl>
      )}

      {!readOnly && (
        <div className="mt-2 flex flex-wrap items-center gap-1">
          {rejected ? (
            <Action
              icon={Undo2}
              label="Restore"
              onClick={() => onAct(bucket, item.id, "confirm")}
            />
          ) : (
            <>
              {item.status === "suggested" && (
                <Action
                  icon={Check}
                  label={bucket === "diagnoses" ? "Accept" : "Confirm"}
                  tone="success"
                  onClick={() => onAct(bucket, item.id, "confirm")}
                />
              )}
              <Action icon={Pencil} label="Edit" onClick={() => setEditing(true)} />
              <Action
                icon={X}
                label={bucket === "diagnoses" ? "Reject" : "Dismiss"}
                onClick={() => onAct(bucket, item.id, "reject")}
              />
              <Action
                icon={Trash2}
                label="Remove"
                tone="danger"
                onClick={() => onAct(bucket, item.id, "remove")}
              />
            </>
          )}

          {showPrescriptionToggle && !rejected && (
            <Button
              type="button"
              variant={item.addedToPrescription ? "secondary" : "outline"}
              size="sm"
              className="ml-auto h-6 px-2 text-[0.7rem]"
              onClick={() =>
                onAct(
                  bucket,
                  item.id,
                  item.addedToPrescription ? "removeFromPrescription" : "addToPrescription",
                )
              }
            >
              {item.addedToPrescription ? "On prescription" : "Add to prescription"}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function Action({
  icon: Icon,
  label,
  tone,
  onClick,
}: {
  icon: typeof Check;
  label: string;
  tone?: "success" | "danger";
  onClick: () => void;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className={cn(
        "h-6 gap-1 px-1.5 text-[0.7rem]",
        tone === "success" && "text-success hover:text-success",
        tone === "danger" && "text-destructive hover:text-destructive",
      )}
      onClick={onClick}
    >
      <Icon className="size-3" aria-hidden />
      {label}
    </Button>
  );
}

function FieldSet({
  bucket,
  values,
  onChange,
}: {
  bucket: ClinicalBucket;
  values: Record<string, string>;
  onChange: (field: string, value: string) => void;
}) {
  const config = DISPLAY[bucket];
  const fields = [config.primary, ...config.details];

  return (
    <div className="space-y-1.5">
      {fields.map((field) => (
        <Input
          key={field}
          value={values[field] ?? ""}
          onChange={(event) => onChange(field, event.target.value)}
          placeholder={field === config.primary ? "Required" : (DETAIL_LABEL[field] ?? field)}
          aria-label={field === config.primary ? config.addLabel : (DETAIL_LABEL[field] ?? field)}
          className="h-7 text-xs"
        />
      ))}
    </div>
  );
}

function AddForm({
  bucket,
  onCancel,
  onSubmit,
}: {
  bucket: ClinicalBucket;
  onCancel: () => void;
  onSubmit: (payload: Record<string, string>) => void;
}) {
  const [values, setValues] = useState<Record<string, string>>({});
  const primary = DISPLAY[bucket].primary;

  return (
    <form
      className="space-y-2 rounded-lg border border-primary/25 bg-card px-3 py-2.5"
      onSubmit={(event) => {
        event.preventDefault();
        if (!values[primary]?.trim()) return;
        onSubmit(values);
      }}
    >
      <FieldSet
        bucket={bucket}
        values={values}
        onChange={(field, value) => setValues((current) => ({ ...current, [field]: value }))}
      />
      <div className="flex gap-1.5">
        <Button type="submit" size="sm" className="h-6 px-2 text-[0.7rem]">
          Add
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-[0.7rem]"
          onClick={onCancel}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}

function EditForm({
  bucket,
  item,
  onCancel,
  onSubmit,
}: {
  bucket: ClinicalBucket;
  item: ClinicalItem;
  onCancel: () => void;
  onSubmit: (patch: Record<string, string>) => void;
}) {
  const config = DISPLAY[bucket];
  const [values, setValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    for (const field of [config.primary, ...config.details]) {
      const value = item[field as keyof ClinicalItem];
      if (typeof value === "string") initial[field] = value;
    }
    return initial;
  });

  return (
    <form
      className="space-y-2 rounded-lg border border-primary/25 bg-card px-3 py-2.5"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit(values);
      }}
    >
      <FieldSet
        bucket={bucket}
        values={values}
        onChange={(field, value) => setValues((current) => ({ ...current, [field]: value }))}
      />
      <div className="flex gap-1.5">
        <Button type="submit" size="sm" className="h-6 px-2 text-[0.7rem]">
          Save
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-[0.7rem]"
          onClick={onCancel}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}
